import {
  IsBoolean,
  IsIn,
  IsISO8601,
  IsOptional,
  IsString,
  MaxLength,
  MinLength,
} from 'class-validator';
import type { MandateSource, ProceedingsOutcome } from './stage-data.dto';

/**
 * Provincial Offences Procedure Module (brief §7 / §9.2 — shared, callable).
 * Triggered from the Type 3 Step 9 sub-flow (and, in future, Types 4/8/10).
 *
 * P1 generates and files penal proceedings against a Government Portal file under a council
 * mandate (`council_resolution`, s. 137) or a standing delegation
 * (`standing_delegation`, s. 72 / s. 80), and — in the same event — produces the
 * court docket entry. P2 is the court docket instrument: one row per filed
 * proceedings, exportable as Word or Excel. P3 is post-filing case management —
 * the officer records the 1st appearance, returnable date, plea, fine imposed
 * and notes back onto the filed matter, which fills docket columns 7-12.
 *
 * The mandate vocabulary is the WEB enum set
 * (`council_resolution` | `standing_delegation`), shared with `Step9Data`'s
 * {@link MandateSource} — not the mobile blueprint's hyphenated values.
 */

/** Lifecycle of a proceedings filing. `returned` = unresolved, back to council. */
export type ProceedingsStatus = 'draft' | 'filed' | 'resolved' | 'returned';

/**
 * P3 — the plea entered by the defendant. A constrained vocabulary rather than
 * free text so the docket column is filterable and the Word/Excel exports read
 * consistently. `none_entered` covers an adjournment with no plea taken yet.
 */
export type ProceedingsPlea = 'guilty' | 'not_guilty' | 'guilty_with_explanation' | 'none_entered';

/** Every {@link ProceedingsPlea}, in the order the officer-facing selector shows them. */
export const PROCEEDINGS_PLEAS = [
  'not_guilty',
  'guilty',
  'guilty_with_explanation',
  'none_entered',
] as const satisfies readonly ProceedingsPlea[];

/** P1 — a proceedings filing on a Government Portal file. */
export interface ProceedingsRecord {
  id: string;
  fileId: string;
  mandateSource: MandateSource;
  /** Whether the municipal clerk countersigned the filing. */
  clerkCountersigned: boolean;
  /** Assigned by the court at filing; null until written back. */
  courtFileNumber: string | null;
  filedAt: string | null;
  status: ProceedingsStatus;
  // ── P3 — post-filing case management (null until the officer records it) ───
  /** First appearance date (ISO 8601). */
  firstAppearance: string | null;
  /** Returnable / next court date (ISO 8601). */
  returnableDate: string | null;
  plea: ProceedingsPlea | null;
  /** Free text — the fine as pronounced (e.g. "$1,200 + costs"). */
  fineImposed: string | null;
  notes: string | null;
}

/**
 * P2 — one docket row per filed proceedings, derived from the proceedings
 * record joined with its file. Carries the brief's 12-column Lawyer/Court
 * Tracking structure (§9.2): the first six are auto-populated at filing; the
 * appearance/plea/fine/notes columns are owned by P3 and are written back by
 * {@link UpdateProceedingsDto} as the matter moves through court.
 */
export interface DocketEntry {
  /** Same id as the proceedings record. */
  id: string;
  fileId: string;
  /** Col 1 — File No. */
  reference: string;
  /** Col 2 — Owner / Defendant. Null when the owner is unknown. */
  defendant: string | null;
  /** Col 3 — Violation. */
  violation: string;
  /** Col 4 — Statute. */
  statute: string;
  /** Col 5 — Date Filed (ISO 8601). */
  filedAt: string;
  /** Col 6 — Court File Number. */
  courtFileNumber: string;
  /** Col 7 — 1st Appearance (P3). */
  firstAppearance: string | null;
  /** Col 8 — Returnable Date (P3). */
  returnableDate: string | null;
  /** Col 9 — Plea (P3). */
  plea: ProceedingsPlea | null;
  /** Col 10 — Fine Imposed (P3). */
  fineImposed: string | null;
  /** Col 11 — Status. */
  status: ProceedingsStatus;
  /** Col 12 — Notes (P3). */
  notes: string | null;
}

/** P1 — file proceedings against a file (the file id rides in the route). */
export class FileProceedingsDto {
  @IsIn(['council_resolution', 'standing_delegation'])
  mandateSource!: MandateSource;

  @IsOptional()
  @IsBoolean()
  clerkCountersigned?: boolean;

  // The court file number assigned to the matter (written onto the docket row).
  @IsString()
  @MinLength(1)
  @MaxLength(64)
  courtFileNumber!: string;
}

/**
 * Resolve a filed matter. `resolved` settles the proceedings (the file closes
 * via the Step 9 escalation flow); `unresolved` returns it for re-deliberation.
 */
export class ResolveProceedingsDto {
  @IsIn(['resolved', 'unresolved'])
  outcome!: ProceedingsOutcome;
}

/**
 * P3 — write post-filing case management back onto a filed matter (docket
 * columns 7-12). Every field is a partial update with three-way semantics:
 *
 * - **omitted** (`undefined`) — leave the stored value untouched;
 * - **`null`** — clear the stored value (a court date that was vacated);
 * - **a value** — set it.
 *
 * `@IsOptional()` skips validation for both `undefined` and `null`, which is
 * exactly the pair we want to wave through.
 */
export class UpdateProceedingsDto {
  @IsOptional()
  @IsISO8601()
  firstAppearance?: string | null;

  @IsOptional()
  @IsISO8601()
  returnableDate?: string | null;

  @IsOptional()
  @IsIn(PROCEEDINGS_PLEAS)
  plea?: ProceedingsPlea | null;

  @IsOptional()
  @IsString()
  @MaxLength(64)
  fineImposed?: string | null;

  @IsOptional()
  @IsString()
  @MaxLength(4000)
  notes?: string | null;
}
